import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { CardLayoutComponent, CardSection } from '../shared/card-layout/card-layout.component';
import { Store } from '@ngrx/store';

@Component({
    selector: 'app-coverages',
    imports: [CardLayoutComponent],
    template: `
        <app-card-layout
            (click)="isActive = !isActive"
            [isActive]="isActive"
            title="Coverages"
            [isLoading]="isDCPolicyLoaded"
            [failed]="dcPolicyFailed"
            [columns]="['COVERAGE NAME', 'LIMIT', 'PREMIUM']"
            [sections]="sections"
            (retry)="retryDCPolicy()"
        ></app-card-layout>
    `,
})
export class CoveragesComponent {
    isActive = false;
    @Input() isDCPolicyLoaded = false;
    @Input() dcPolicyFailed = false;
    @Output() retry = new EventEmitter<void>();
    private store = inject(Store);
    policySignal = this.store.selectSignal((state: any) => state.policy);

    private formatCurrency(value: any): string {
        const numeric = typeof value === 'string' ? value.replace(/,/g, '') : value;
        const num = Number(numeric);
        const safe = isNaN(num) ? 0 : num;
        const abs = Math.abs(safe).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        return safe < 0 ? `-$${abs}` : `$${abs}`;
    }

    private isNumeric(value: any): boolean {
        if (value === null || value === undefined || value === '') return false;
        return !Number.isNaN(Number(String(value).replace(/,/g, '')));
    }

    get sections(): CardSection[] {
        const policyState = this.policySignal();
        const policyNum = policyState.policyNumber;
        const coverages = policyState.data?.[policyNum]?.policySnapshot?.coverages;
        if (!coverages) return [];

        const policyGeneralInfo = policyState.data?.[policyNum]?.policySnapshot?.policyGeneralInformation;
        const packageTypeField = policyGeneralInfo?.packageType
            ? [{ caption: policyGeneralInfo.packageType.node ?? 'Package', value: policyGeneralInfo.packageType.value ?? '-', premium: ' ' }]
            : [];

        return [
            { title: 'Must haves', icon: 'must-haves', fields: [...packageTypeField, ...this.mapFields(coverages.mustHaves, 'mustHaves')] },
            { title: 'Enhanced', icon: 'nice-to-haves', fields: this.mapFields(coverages.enhanced, 'enhanced') },
            { title: 'Nice to have', icon: 'enhanced', fields: this.mapFields(coverages.niceToHave, 'niceToHave') },
        ].filter((s) => s.fields.length > 0);
    }

    private mapFields(data: any, section: string) {
        if (!data) return [];
        return Object.values(data).map((c: any) => {
            let value = c.value;

            // If coverage is not included, always show "Not included" regardless of the value
            if (c.coverageIncluded === 'false' || c.coverageIncluded === false) {
                value = 'Not included';
            } else if (section === 'mustHaves') {
                if (c.node === '$5,000 Theft Limitation') {
                    value = value === 'Included' || parseInt(value) === 1 ? 'Included' : 'Not included';
                } else {
                    value = this.isNumeric(value) ? this.formatCurrency(value) : value ?? '-';
                }
            } else if (section === 'enhanced') {
                if (this.isNumeric(value)) {
                    value = this.formatCurrency(value);
                } else {
                    value = value ?? '-';
                }
            } else if (section === 'niceToHave') {
                if (
                    c.node === 'Increased Personal Computer Systems' ||
                    c.node === 'Increased Personal Computer Programs'
                ) {
                    if (this.isNumeric(value) && Number(String(value).replace(/,/g, '')) > 0) {
                        value = this.formatCurrency(value);
                    } else {
                        value = 'Not included';
                    }
                } else if (parseInt(value) === 0) {
                    value = 'Not included';
                } else {
                    value = this.isNumeric(value) ? this.formatCurrency(value) : value ?? '-';
                }
            }
            return {
                caption: c.node ?? '-',
                value: value ?? '-',
                premium: this.formatCurrency(Number(c.premium ?? 0).toFixed(2)),
            };
        });
    }

    retryDCPolicy() {
        this.retry.emit();
    }
}
